import { useEffect, useState } from 'react';
import { getProductOptions, getProductTargets, ProductOptions, ProductTargets } from '../../api/assets';

// 冷缓存时的重试节奏。后端 `/v1/assets/products/options` 冷算要"两三分钟"
// (assetproduct/handler.go), 它给了自己 25s 预算, 到点先返回 200 + 空数组 +
// `warming: true`, 后台那把继续算完写缓存。
//
// 12 次 × 15s ⇒ 最后一次重试落在 t=165s, 盖得住"三分钟"那一头。
// **别按 `次数 × 间隔` 心算覆盖窗口**: 首拉在 t=0 就用掉一次计数, 所以窗口是
// (N-1)×间隔 而不是 N×间隔 —— 上一版写 8 次并在注释里说"覆盖约 2 分钟",
// 实际只到 105s, 被 code review 抓出来。
const WARMING_RETRY_MS = 15_000;
const WARMING_MAX_TRIES = 12;

/**
 * 拉一个"冷算时先回 warming 占位"的候选接口, 并在 warming / 出错时按上面的节奏重试。
 *
 * **必须认 `warming` 并重试** —— 不认的话冷缓存时下拉全是空的, 而接口返回的是 200,
 * 页面其余部分正常, **没有任何错误信号**。2026-10-08 在 dev 上真实发生过:
 * 库里明明有 61 个采集类目, 下拉却是空的。
 *
 * **出错也要重试** (2026-10-10): 后台冷算被掐断时会挂 5 分钟失败标记, 期间接口直接报错。
 * 原来 `.catch` 吞掉错误就不再拉, 页面停在上一次 warming 的空数组上, 一样是静默的空下拉。
 */
function useWarmingFetch<T extends { warming?: boolean }>(fetcher: () => Promise<T>): T | null {
    const [data, setData] = useState<T | null>(null);
    useEffect(() => {
        let alive = true;
        let tries = 0;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const retry = () => {
            if (++tries < WARMING_MAX_TRIES) timer = setTimeout(pull, WARMING_RETRY_MS);
        };
        const pull = () => {
            fetcher().then((o) => {
                if (!alive) return;
                setData(o);
                if (o.warming) retry();
            }).catch(() => { if (alive) retry(); });
        };
        pull();
        // 清掉在排队的那次重试: 只置 alive 的话, 卸载后仍会多发一次请求再把结果丢掉.
        return () => { alive = false; clearTimeout(timer); };
        // fetcher 是模块级 API 函数, 引用稳定; 只在挂载时拉一次.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return data;
}

/**
 * 商品采集中心列表页的四个筛选候选 (采集类目 / 品牌 / 类目路径 / 材质)。
 *
 * **只要采集类目的页面别用它**, 用下面的 useProductTargets —— 这份要陪着品牌 / 类目路径 /
 * 材质三组全表聚合一起算, 任何一组被掐断类目也跟着拿不到 (2026-10-10 美服打标审核页踩过)。
 */
export function useProductOptions(): ProductOptions | null {
    return useWarmingFetch(getProductOptions);
}

/**
 * 只拉采集类目候选 (`/v1/assets/products/targets`, 只扫 idx_target_rank, 不缓存、每次直查)。
 * 不会回 warming; 走 useWarmingFetch 是为了出错时自动重试。
 *
 * 选项**来自数据**而不是前端写死的词表: 审核页的类目下拉曾是 35 条手工词表,
 * 采集侧铺到 61 个类目时它还停在 35 个, 40 个类目根本筛不到 (2026-10-08)。
 */
export function useProductTargets(): ProductTargets | null {
    return useWarmingFetch(getProductTargets);
}
